import { Box, Typography, Dialog, useTheme } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Button } from 'components'
import { Icon } from 'packages/eid-icons'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import TimeConstraintComp from './TimeConstraint'
import { Spinner, Tooltip } from 'packages/eid-ui'
import { isNilOrEmpty } from 'packages/core'

const StyledBox = styled(Box)(({ theme }) => ({
    '& h2': {
        fontSize: '1.8rem',
        color: '#1b1f1f',
        fontWeight: '600',
    },
    '& .constraintModalHeader': {
        padding: '3.2rem',
    },
    '& .assignmentModalHeader': {
        padding: '3.2rem',
    },
    '& .assignmentModalHeading': {
        padding: '1.6rem 3.2rem',
        borderTop: '0.1rem solid #0000002e !important',
        borderBottom: '0.1rem solid #0000002e !important',
        backgroundImage:
            'linear-gradient(0deg, rgb(0, 0, 0, 0.02) 12.5%, rgb(255, 255, 255) 12.5%, rgb(255, 255, 255) 50%, rgb(0, 0, 0, 0.02) 50%, rgb(0, 0, 0, 0.02) 62.5%, rgb(255, 255, 255) 62.5%, rgb(255, 255, 255) 100%) !important',
        backgroundSize: '0.8rem 0.8rem !important',
    },
    '& .assignmentModalInner': {
        padding: '3.2rem',
    },
    '& .assignmentModalFooter': {
        padding: '2.4rem 3.2rem',
        boxShadow: '0 0.3rem 0.7rem 0.4rem rgba(0, 0, 0, 0.08)',
        '& button': {
            border: `solid 0.1rem ${theme.palette.primary.main}`,
            padding: '0.9rem 2.4rem',
            '& svg': {
                marginRight: '1rem',
            },
        },
    },
    '& .assignmentTimeInner': {
        padding: '2rem 3.2rem',
        paddingTop: '0',
    },
}))

const CustomDialog = styled(Dialog)(({}) => ({
    '& .MuiDialog-paperWidthSm': {
        maxWidth: '43rem',
        width: '100%',
        background: '#f7f8fa',
        '& .MuiBox-root': {
            maxWidth: '83rem',
            width: '100%',
        },
    },
    '& .MuiBox-root': {
        border: 'none',
        background: ' #f7f8fa',
        position: 'relative',
    },
}))

const CloseIconBox = styled(Box)({
    '& svg': {
        position: 'absolute',
        top: '-1rem',
        right: '-1rem',
        cursor: 'pointer',
    },
})

const AddTimeConstraint = ({
    btnLabel,
    iconName,
    startTime,
    endTime,
    onSave = () => {},
    saving = false,
    disabledTooltip = '',
    autoCloseOnSave = true,
    closeAfterSave = false,
    maximumValueInMinutes = null,
}) => {
    const theme = useTheme()
    const [openAddTimeConstraintModal, setOpenAddTimeConstraintModal] =
        useState(false)
    const { t } = useTranslation()
    const [{ selectedStartDate, selectedEndDate }, setSelectedDate] = useState({
        selectedStartDate: startTime,
        selectedEndDate: endTime,
    })
    const [timeConstrained, setTimeConstrained] = useState(
        selectedEndDate && selectedStartDate ? true : false,
    )

    // Track initial state to detect changes
    const [initialState, setInitialState] = useState({
        timeConstrained: selectedEndDate && selectedStartDate ? true : false,
        selectedStartDate: startTime,
        selectedEndDate: endTime,
    })

    useEffect(() => {
        if (timeConstrained) {
            const currentDatetime = startTime
                ? startTime
                : moment.utc().second(0)
            const endDatetime = endTime ? endTime : null
            setSelectedDate({
                selectedStartDate: currentDatetime,
                selectedEndDate: endDatetime,
            })
        } else {
            setSelectedDate({
                selectedStartDate: null,
                selectedEndDate: null,
            })
        }
    }, [startTime, endTime, timeConstrained])

    // Update initial state when props change
    useEffect(() => {
        setInitialState({
            timeConstrained: endTime && startTime ? true : false,
            selectedStartDate: startTime,
            selectedEndDate: endTime,
        })
    }, [startTime, endTime])

    const handleStartDate = (startDateToSet) => {
        setSelectedDate((prev) => {
            return { ...prev, selectedStartDate: startDateToSet }
        })
    }

    const handleEndDate = (endDateToSet) => {
        setSelectedDate((prev) => {
            return { ...prev, selectedEndDate: endDateToSet }
        })
    }

    const hasInvalidDates =
        timeConstrained &&
        (isNilOrEmpty(selectedEndDate) ||
            moment.utc(selectedStartDate).isAfter(moment.utc(selectedEndDate)))

    let invalidDatesMessage = ''

    if (hasInvalidDates) {
        if (isNilOrEmpty(selectedEndDate)) {
            invalidDatesMessage = t('InvalidEndDateTime')
        }
        if (
            moment.utc(selectedStartDate).isAfter(moment.utc(selectedEndDate))
        ) {
            invalidDatesMessage = t('EndDateGreaterThanStartDate')
        }
    }

    // Check if changes have been made from initial state
    const hasChanges = () => {
        // Case 1: timeConstrained changed
        if (timeConstrained !== initialState.timeConstrained) {
            return true
        }

        // Case 2: timeConstrained is false (no time constraint)
        if (!timeConstrained) {
            return false // No changes needed when timeConstrained is false
        }

        // Case 3: timeConstrained is true, check if dates are selected and different from initial
        if (timeConstrained) {
            const startDateChanged = !moment(selectedStartDate).isSame(
                moment(initialState.selectedStartDate),
            )
            const endDateChanged = !moment(selectedEndDate).isSame(
                moment(initialState.selectedEndDate),
            )
            return startDateChanged || endDateChanged
        }

        return false
    }

    // Check if Save button should be disabled
    const isSaveDisabled = () => {
        // If there are invalid dates, disable save
        if (hasInvalidDates) {
            return true
        }

        // If timeConstrained is true but no dates are selected, disable save
        if (
            timeConstrained &&
            (isNilOrEmpty(selectedStartDate) || isNilOrEmpty(selectedEndDate))
        ) {
            return true
        }

        // If no changes have been made, disable save
        if (!hasChanges()) {
            return true
        }

        return false
    }

    // Get tooltip message for disabled Save button
    const getSaveButtonTooltip = () => {
        if (!isSaveDisabled()) {
            return ''
        }

        // If there are invalid dates, show validation message
        if (hasInvalidDates) {
            return invalidDatesMessage
        }

        // If timeConstrained is true but no dates are selected
        if (
            timeConstrained &&
            (isNilOrEmpty(selectedStartDate) || isNilOrEmpty(selectedEndDate))
        ) {
            if (
                isNilOrEmpty(selectedStartDate) &&
                isNilOrEmpty(selectedEndDate)
            ) {
                return t('SelectStartDateAndEndDate')
            } else if (isNilOrEmpty(selectedStartDate)) {
                return t('SelectStartDate')
            } else {
                return t('SelectEndDate')
            }
        }

        // If no changes have been made
        if (!hasChanges()) {
            return t('NoChangesToSave')
        }

        return ''
    }

    const handleModalClose = () => {
        setSelectedDate((prev) => {
            return {
                ...prev,
                selectedEndDate: endTime,
                selectedStartDate: startTime,
            }
        })

        setTimeConstrained(endTime && startTime ? true : false)

        // Reset initial state to current props
        setInitialState({
            timeConstrained: endTime && startTime ? true : false,
            selectedStartDate: startTime,
            selectedEndDate: endTime,
        })

        setOpenAddTimeConstraintModal(false)
    }

    useEffect(() => {
        if (closeAfterSave) {
            setOpenAddTimeConstraintModal(false)
        }
    }, [closeAfterSave])

    return (
        <>
            <Tooltip title={disabledTooltip}>
                <Box>
                    <Button
                        disabled={Boolean(disabledTooltip)}
                        onClick={() => setOpenAddTimeConstraintModal(true)}
                        sx={{
                            borderRadius: '0.4rem',
                            padding: '0.6rem 0.7rem',
                            backgroundColor: theme.palette.primary.main,
                            color: theme.palette.common.white,
                            fontSize: '1.4rem',
                            minWidth: '2.4rem',
                            '&:hover': {
                                backgroundColor: theme.palette.primary.main,
                            },
                            '&:disabled': {
                                backgroundColor: '#aab0b4 !important',
                                borderColor: '#aab0b4 !important',
                                color: '#ffffff',
                            },
                            '& svg': {
                                width: '1.6rem',
                                height: '1.6rem',
                                '& path': {
                                    color: theme.palette.common.white,
                                    stroke: theme.palette.common.white,
                                },
                            },
                        }}
                    >
                        {iconName ? (
                            <Icon name={iconName} />
                        ) : btnLabel ? (
                            t(btnLabel)
                        ) : (
                            ''
                        )}
                    </Button>
                </Box>
            </Tooltip>
            <CustomDialog
                open={openAddTimeConstraintModal}
                onClose={() => handleModalClose()}
                title={t('')}
            >
                <StyledBox>
                    <Box className="constraintModalHeader">
                        <CloseIconBox onClick={() => handleModalClose()}>
                            <Icon
                                name="Close"
                                width="1.6rem"
                                height="1.6rem"
                                color="#959598"
                            />
                        </CloseIconBox>
                        <Typography variant="h2">
                            {t('TimeConstraint')}
                        </Typography>
                    </Box>
                    <TimeConstraintComp
                        handleStartDate={handleStartDate}
                        handleEndDate={handleEndDate}
                        selectedStartDate={selectedStartDate}
                        selectedEndDate={selectedEndDate}
                        hasInvalidDates={hasInvalidDates}
                        validationMessage={invalidDatesMessage}
                        timeConstrained={timeConstrained}
                        handleTimeConstraint={setTimeConstrained}
                        maxEndDate={
                            maximumValueInMinutes
                                ? moment(selectedStartDate).add(
                                      maximumValueInMinutes,
                                      'minute',
                                  )
                                : null
                        }
                    />
                    <Box className="assignmentModalFooter">
                        <Tooltip title={getSaveButtonTooltip()}>
                            <span>
                                <Button
                                    sx={{
                                        backgroundColor:
                                            theme.palette.primary.main,
                                            color: theme.palette.common.white,
                                    }}
                                    onClick={() => {
                                        onSave({
                                            selectedStartDate: selectedStartDate
                                                ? moment(selectedStartDate)
                                                      .utc()
                                                      .format()
                                                : null,
                                            selectedEndDate: selectedEndDate
                                                ? moment(selectedEndDate)
                                                      .utc()
                                                      .format()
                                                : null,
                                        })
                                        if (autoCloseOnSave) {
                                            setOpenAddTimeConstraintModal(false)
                                        }
                                    }}
                                    backgroundColor={theme.palette.primary.main}
                                    disabled={isSaveDisabled()}
                                    disabledStyles={{
                                        backgroundColor: '#aab0b4 !important',
                                        borderColor: '#aab0b4 !important',
                                        color: `${theme.palette.common.white} !important`,
                                        display: 'inline-block',
                                    }}
                                >
                                    {!saving ? (
                                        <Icon
                                            name="Save"
                                            width="1.6rem"
                                            height="1.6rem"
                                            color={theme.palette.common.white}
                                        />
                                    ) : (
                                        <Spinner
                                            style={{ marginRight: '1rem' }}
                                            size={0.8}
                                            color={theme.palette.common.white}
                                        />
                                    )}
                                    {t('Save')}
                                </Button>
                            </span>
                        </Tooltip>
                        <Button
                            onClick={() => handleModalClose()}
                            sx={{ marginLeft: '2rem' }}
                        >
                            {t('Cancel')}
                        </Button>
                    </Box>
                </StyledBox>
            </CustomDialog>
        </>
    )
}

export default AddTimeConstraint
